import { init as initApm } from "@elastic/apm-rum";
import * as Sentry from "@sentry/react";
import React, {
  ReactNode,
  createContext,
  useState,
  Fragment,
  memo,
  useMemo,
  useEffect,
  useContext,
} from "react";
import { Provider as UrqlProvider } from "urql";
import { QueryParamProvider } from "use-query-params";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { Client as Styletron } from "styletron-engine-atomic";
import { Provider as StyletronProvider } from "styletron-react";
import { BaseProvider } from "baseui";
import baseuiTheme from "../components/styling/baseui_theme";
import { ThemeProvider as MaterialThemeProvider, THEME_ID } from "@mui/material/styles";
import materialTheme from "../components/styling/material_theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "./icons";
import RegistrationOptions from "./registration_options";
import deprecatedTheme, { Theme } from "./styling/deprecated_theme";
import { ModalProvider } from "./components/layout/modal";
import { SnackbarProvider } from "./components/base_ui/progress_and_validation/snackbar";
import Alert, { AlertProvider, AlertType, AlertDuration } from "./components/layout/alert";
import { UserInfoForApmDocument } from "./generated/graphql";
// this is used to polyfill fetch in our test environment
import "whatwg-fetch";
import { gql } from "@urql/core";
import getCurrentParticipant from "./participant_dashboard/shared/get_current_participant";
import { AbTestProvider } from "./shared/use_ab_test_segment";
import useUrqlClient from "./use_urql_client";
import { ssoRecruitmentLinkDatum } from "../entrypoints/participant_dashboard";
import { SinglePageProvider } from "./components/app/single_page";
import { DURATION } from "baseui/snackbar";
import { Routes, Route, BrowserRouter } from "react-router";
import Login from "./login";
import ProtectedRoute from "./shared/routing/protected_route";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import queryString from "query-string";
import { useQuery } from "urql";

// import.meta.env should actually always be defined (we get it from vite https://vitejs.dev/guide/env-and-mode.html#env-variables)
// however, when running in storybook we're still using webpack for the time being. So we need to safegaurd against the case of env being undefined
let environment = import.meta.env?.MODE ?? "development";
try {
  // since when deploying to Heroku, the build step actually happens on staging, we need some way to differentiate between staging and production
  // for the moment, we're using the hostname to determine this
  if (window.location.hostname.includes("mentorcollective.org")) {
    environment = "production";
  } else if (window.location.hostname.includes("shearwater.me")) {
    environment = "staging";
  } else if (window.location.hostname.includes("mcdemo.me")) {
    environment = "demo";
  }
} catch {
  // we probably don't need this - but just being extra careful in case this ever runs in a non-browser environment
  // and there's no window.location
}
const apm = initApm({
  serviceName: "frontend",
  serverUrl: "https://2044674e4756433f967095d0f5004fc3.apm.us-east-1.aws.cloud.es.io:443",
  environment: environment,
  // this is necessary to enable sourcemaps for APM, but we we were having issues so commenting out for now
  // serviceVersion: process.env.SOURCE_VERSION || "unknown",
  active: ["production", "staging", "demo"].includes(environment),
});

// Setting this to enabled: false doesn't prevent all overhead from Sentry instrumentation.
// To disable Sentry completely, depending on environment, call Sentry.init conditionally.
if (["production", "staging", "demo"].includes(environment)) {
  Sentry.init({
    dsn: "https://c3f131051496079ebfae04d839e58929@o4507534093320192.ingest.us.sentry.io/4507534667874304",
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 0.02, //  1.0 means capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["mentorcollective.org", "shearwater.me", "mcdemo.me"],
    // Session Replay
    replaysSessionSampleRate: 0.0, // You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.005, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

interface AlertState {
  message: string;
  type: AlertType;
  duration?: AlertDuration;
}

export enum Environment {
  Production = "production",
  Development = "development",
  Staging = "staging",
  Test = "test",
  Demo = "demo",
  Performance = "performance",
}

export const AppContext = createContext<{
  userId: string;
  envBanner?: string;
  csrfToken: string;
  signIn: ({ email, password }: { email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
  triggerAlert: (alert: AlertState) => void;
  school?: {
    name: string;
    logo: string;
    isSsoEnabled?: boolean;
    isSsoTestingEnabled?: boolean;
    ssoConnection?: string;
    ssoBrand?: string;
    id: string;
    shortName?: string;
    ssoMentorRecruitmentLinkData?: ssoRecruitmentLinkDatum[];
    ssoMenteeRecruitmentLinkData?: ssoRecruitmentLinkDatum[];
  };
  programId: string;
  environment: Environment;
}>({
  userId: "",
  csrfToken: "",
  programId: "",
  signIn: async () => undefined,
  signOut: async () => undefined,
  triggerAlert: () => undefined,
  environment: Environment.Development,
});

const EnvironmentBannerWrapper = styled.span`
  .staging {
    background-color: ${({ theme }) => theme.colors.backgroundWarning};
    color: ${({ theme }) => theme.colors.contentPrimary};
  }
  .preview {
    background-color: ${({ theme }) => theme.colors.backgroundPositive};
    color: ${({ theme }) => theme.colors.contentOnColor};
  }
  .test {
    background-color: ${({ theme }) => theme.colors.accent500};
    color: ${({ theme }) => theme.colors.contentOnColor};
  }
  .development {
    background-color: ${({ theme }) => theme.colors.backgroundInverseSecondary};
    color: ${({ theme }) => theme.colors.contentOnColor};
  }
`;

const EnvironmentBannerContainer = styled.div`
  position: sticky;
  z-index: ${({ theme }) => theme.zIndex.modal - 1};
  top: 0;
  left: 0;
  right: 0;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.scale600} ${({ theme }) => theme.sizing.scale800};

  font-weight: ${({ theme }) => theme.typography.HeadingXSmall.fontWeight};
  font-family: ${({ theme }) => theme.typography.HeadingXSmall.fontFamily};
  font-size: ${({ theme }) => theme.typography.HeadingXSmall.fontSize};
  line-height: ${({ theme }) => theme.typography.HeadingXSmall.lineHeight};
`;

const AppStyles = createGlobalStyle`
  body {
    margin: 0;
    font-family: ${({ theme }: { theme: Theme }) => theme.font.family.body};
    font-size: ${({ theme }) => theme.font.size.body.one};
    color: ${({ theme }) => theme.deprecatedColors.text.normal};

    /* fix the body so we don't get weird scrolling when a modal is open
     * see app/javascript/components/shared/use_body_class.ts hook
    */
    &.disable-scrolling {
      overflow: hidden;
      height: 100%;
    }

    h1 {
      font-family: ${({ theme }) => theme.font.family.header};
      font-size: ${({ theme }) => theme.font.size.header.one};
      font-weight: ${({ theme }) => theme.font.weight.normal};
    }

    h2 {
      font-family: ${({ theme }) => theme.font.family.header};
      font-size: ${({ theme }) => theme.font.size.header.two};
      font-weight: ${({ theme }) => theme.font.weight.normal};
    }

    h3 {
      font-family: ${({ theme }) => theme.font.family.header};
      font-size: ${({ theme }) => theme.font.size.header.three};
      font-weight: ${({ theme }) => theme.font.weight.normal};
    }

    h4 {
      font-family: ${({ theme }) => theme.font.family.header};
      font-size: ${({ theme }) => theme.font.size.header.four};
      font-weight: ${({ theme }) => theme.font.weight.normal};
    }

    h5 {
      font-family: ${({ theme }) => theme.font.family.header};
      font-size: ${({ theme }) => theme.font.size.header.five};
      font-weight: ${({ theme }) => theme.font.weight.normal};
    }

    h6 {
      font-family: ${({ theme }) => theme.font.family.header};
      font-size: ${({ theme }) => theme.font.size.header.six};
      font-weight: ${({ theme }) => theme.font.weight.normal};
    }

    /* applied to elements we only want visible to screen readers, recommended via https://accessibility.18f.gov/hidden-content/*/
    sr-only {
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }
  }
`;

gql`
  query UserInfoForApm($userId: ID!) {
    user(id: $userId) {
      id
      email
      isPartner
      isAdmin
      participants {
        id
        registrationDate
        program {
          id
        }
      }
    }
  }
`;

// we use this wrapper to add some extra info to our APM logs once we know who's signed in
// this wrapper does query GraphQL, but (importantly) it does NOT prevent the rest of the page
// from rendering while that request is loading
function ApmContext({ children }: { children: ReactNode }) {
  const { userId } = useContext(AppContext);
  const [{ data }] = useQuery({
    query: UserInfoForApmDocument,
    variables: { userId },
    pause: typeof userId === "undefined" || userId.length <= 0,
  });
  useEffect(() => {
    if (data?.user) {
      const user = data.user;
      apm.setUserContext({ id: user.id, email: user.email || "" });
      const isStudent = user.participants.some(
        (participant) => participant.__typename === "Student",
      );
      const isMentor = user.participants.some((participant) => participant.__typename === "Mentor");
      // eslint-disable-next-line
      // @ts-ignore the type for apm package is wrong
      apm.addLabels({ is_partner: user.isPartner, is_admin: user.isAdmin });
      if (isStudent || isMentor) {
        const mostRecentParticipant = getCurrentParticipant(user);
        apm.addLabels({
          participant_type: isStudent && isMentor ? "both" : isStudent ? "student" : "mentor",
          // for now we need to just pick one program - elasticsearch can support array fields,
          // but APM currently does not https://github.com/elastic/apm/issues/82
          most_recent_program_id: mostRecentParticipant.program.id,
        });
      }
    }
  }, [data]);

  return <Fragment>{children}</Fragment>;
}

interface AppProps {
  initialUserId?: string;
  envBanner?: string;
  urlBase?: string;
  children: ReactNode;
  pageName?: string;
  preventLoginRedirect?: boolean;
  reloadOnLogin?: boolean;
  // currently we just use this to make sure we can style the login page,
  // based on whatever school is tied to the subdomain. Eventually if we start
  // using the school to drive more functionality, we might want to consider
  // using graphql to provide this data in a more structured way
  school?: {
    name: string;
    logo: string;
    isSsoEnabled?: boolean;
    isSsoTestingEnabled?: boolean;
    ssoConnection?: string;
    id: string;
    shortName?: string;
    ssoMentorRecruitmentLinkData?: ssoRecruitmentLinkDatum[];
    ssoMenteeRecruitmentLinkData?: ssoRecruitmentLinkDatum[];
  };
  programId?: string;
  theme?: Theme;
  initializeThemeProviders?: boolean;
}

const MemoApp = memo(function _App({
  triggerAlert,
  initialUserId,
  envBanner,
  urlBase,
  children,
  pageName = "MC Page",
  preventLoginRedirect = false,
  reloadOnLogin = false,
  school,
  programId,
}: AppProps & { triggerAlert: (alert: AlertState) => void }) {
  const initialCsrfToken = document
    ?.querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content");
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [loginData, setLoginData] = useState({
    userId: initialUserId,
    csrfToken: initialCsrfToken,
  });
  // add a field to our APM logs so we can tell what page we're on
  apm.addLabels({ page: pageName });
  apm.setUserContext({ id: initialUserId });
  apm.setInitialPageLoadName(pageName);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const client = useUrqlClient({ csrfToken: loginData.csrfToken });

  return (
    <UrqlProvider value={client}>
      <BrowserRouter basename={urlBase}>
        <QueryParamProvider
          adapter={ReactRouter6Adapter}
          options={{
            searchStringToObject: queryString.parse,
            objectToSearchString: queryString.stringify,
          }}
        >
          <AppStyles theme={{ ...deprecatedTheme, ...baseuiTheme }} />
          <AbTestProvider>
            <ModalProvider>
              <SinglePageProvider>
                <SnackbarProvider defaultDuration={DURATION.medium}>
                  <AppContext.Provider
                    value={{
                      csrfToken: loginData.csrfToken || "",
                      userId: loginData.userId || "",
                      envBanner,
                      school,
                      triggerAlert,
                      programId: programId || "",
                      environment: environment as Environment,
                      signIn: async ({ email, password }) => {
                        const csrfToken = loginData.csrfToken || "";
                        const signInResponse = await fetch("/api/users/sign_in", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": `${csrfToken}`,
                          },
                          redirect: "error",
                          credentials: "same-origin",
                          body: JSON.stringify({
                            api_user: {
                              email,
                              password,
                            },
                          }),
                        });
                        const responseJson: { user_id: number } = await signInResponse.json();
                        setLoginData({
                          userId: responseJson.user_id.toString(),
                          csrfToken: signInResponse.headers.get("X-CSRF-Token") || "",
                        });
                      },
                      signOut: async () => {
                        const csrfToken = loginData.csrfToken || "";
                        await fetch("/api/users/sign_out", {
                          method: "DELETE",
                          headers: {
                            "X-CSRF-Param": "authenticity_token",
                            "X-CSRF-Token": `${csrfToken}`,
                          },
                        });
                      },
                    }}
                  >
                    <ApmContext>
                      <Routes>
                        <Route
                          path="/*"
                          element={
                            <ProtectedRoute
                              isAllowed={!!loginData.userId || preventLoginRedirect}
                              redirectPath="/sign_in"
                            >
                              <>{children}</>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="sign_in/*"
                          element={
                            <ProtectedRoute isAllowed={!loginData.userId} redirectPath={"/"}>
                              <Login
                                onLogin={() => {
                                  // If we are entering the app through a contextless url,
                                  // reload to have the backend handle where the user should go.
                                  if (reloadOnLogin) {
                                    window.location.reload();
                                  }
                                }}
                              />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="registration_options"
                          element={
                            <ProtectedRoute isAllowed={!loginData.userId} redirectPath={"/"}>
                              <RegistrationOptions />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </ApmContext>
                  </AppContext.Provider>
                </SnackbarProvider>
              </SinglePageProvider>
            </ModalProvider>
          </AbTestProvider>
        </QueryParamProvider>
      </BrowserRouter>
    </UrqlProvider>
  );
});

const engine = new Styletron();

// we want to make sure the entire app doesn't re-render every time we trigger an alert. To do that, we memoize the app component
export default function App({ initializeThemeProviders = true, ...rest }: AppProps) {
  const [alert, setAlert] = useState<AlertState | undefined>();
  // we additionally need to memoize the "triggerAlert" function, so that it doesn't cause the <MemoApp /> to re-render
  const triggerAlert = useMemo(() => {
    return (alert: AlertState) => setAlert(alert);
  }, [setAlert]);

  const content = (
    <>
      {rest.envBanner && (
        <EnvironmentBannerWrapper>
          <EnvironmentBannerContainer className={rest.envBanner}>
            You are in the Mentor Collective {rest.envBanner} environment - not production.
          </EnvironmentBannerContainer>
        </EnvironmentBannerWrapper>
      )}
      <AlertProvider>
        <MemoApp {...rest} triggerAlert={triggerAlert} />
        {alert && (
          <Alert onDone={() => setAlert(undefined)} type={alert.type} duration={alert.duration}>
            {alert.message}
          </Alert>
        )}
      </AlertProvider>
    </>
  );

  // Some legacy pages don't use App as an entrypoint but their styling now requires the theme providers.
  // Multiple initializations of the theme providers causes issues so we need a way to
  // skip initialization here if it has already been done elsewhere.
  // Only set initializeThemeProviders to `false` if the console is showing a warning that
  // multiple Layer managers are being nested and may cause unwanted side effects.
  if (initializeThemeProviders) {
    return (
      <StyletronProvider value={engine}>
        <BaseProvider theme={baseuiTheme}>
          <ThemeProvider theme={{ ...deprecatedTheme, ...baseuiTheme }}>
            <MaterialThemeProvider theme={{ [THEME_ID]: materialTheme }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>{content}</LocalizationProvider>
            </MaterialThemeProvider>
          </ThemeProvider>
        </BaseProvider>
      </StyletronProvider>
    );
  } else {
    return content;
  }
}
